import { type JSX, type FC, useMemo, useEffect } from 'react';
import { Provider, useUnit } from 'effector-react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Listbox } from '../../../components/listbox';
import { withControl } from '../../../hoc/withControl';
import type { GeneralControlProps, CustomControlProps } from '../../../types/control';
import {
  $data,
  $pending,
  createScope,
  getPropertyTypes as getPropertyTypesEvent,
} from '../model';
import type { PropertyType, Props, PropertyTypeItem } from '../types';

export const PropertyTypesControlScoped: FC<
  GeneralControlProps & CustomControlProps<Props>
> = ({
  value,
  setValue,
  error,
  label,
  required,
  readOnly,
  disabled,
  withClientType = false,
  disableClearable = false,
  sx = {},
  mode,
  onlyActual = false,
  parentAreaId = null,
  excludeOptions = [],
  sxAutocomplete = {},
  isError,
  includeClientType,
  showActiveClientType,
  scope = [],
  isAvailable,
  forAreaId,
}) => {
  const memoScope = useMemo(() => scope, [scope.join(',')]);

  const [data, pending, getPropertyTypes] = useUnit([
    $data,
    $pending,
    getPropertyTypesEvent,
  ]);

  useEffect(() => {
    getPropertyTypes({
      withClientType,
      mode,
      onlyActual,
      parentAreaId,
      includeClientType,
      showActiveClientType,
      scope: memoScope,
      isAvailable,
      forAreaId,
    });
  }, [
    withClientType,
    mode,
    onlyActual,
    parentAreaId,
    includeClientType,
    showActiveClientType,
    memoScope,
    isAvailable,
    forAreaId,
  ]);

  const list = onlyActual && !parentAreaId ? [] : Array.isArray(data) ? data : [];

  const handleFilterOptions = (
    opts: PropertyTypeItem[],
    { inputValue }: { inputValue: string }
  ) => {
    const currentOptions = excludeOptions.length
      ? opts.reduce(
          (acc: PropertyTypeItem[], option) =>
            !excludeOptions.includes(option.id) ? [...acc, option] : acc,
          []
        )
      : opts;

    if (inputValue) {
      return currentOptions.filter((opt) =>
        opt?.label?.toUpperCase().includes(inputValue.toUpperCase())
      );
    }

    return currentOptions;
  };

  return (
    <FormControl required={required} disabled={disabled} sx={sx}>
      {label && <FormLabel error={isError}>{label}</FormLabel>}
      <Autocomplete
        filterOptions={handleFilterOptions}
        disableClearable={disableClearable}
        multiple
        disableCloseOnSelect
        loading={pending}
        value={
          !list
            ? []
            : list.filter(
                (item) =>
                  value
                    .map((i: PropertyType) => (typeof i === 'object' ? i.id : i))
                    .indexOf(item.id) > -1
              )
        }
        options={list}
        onChange={(_: unknown, newValue: any) => {
          setValue(newValue.map((item: PropertyType) => item));
        }}
        slotProps={{
          listbox: {
            component: Listbox,
          },
        }}
        renderInput={(params): JSX.Element => (
          <TextField
            {...params}
            slotProps={{
              input: {
                ...params.InputProps,
                readOnly,
                error: isError,
                endAdornment: readOnly ? null : (
                  <>
                    {pending && <CircularProgress size={20} color="inherit" />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        readOnly={readOnly}
        disabled={disabled}
        sx={sxAutocomplete}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};

export const PropertyTypesControl = withControl<Props & { id: string }>(
  ({ id, ...props }) => {
    const scope = createScope(id);

    return (
      <Provider value={scope}>
        <PropertyTypesControlScoped {...props} />
      </Provider>
    );
  }
);
